import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '..';
import ChildrenAgeSelect from './ChildrenAgeSelect';

import type { RoomInputCounterData, RoomInputData } from './types';

interface IRoomInputCounterProps {
  id: number;
  category: 'adults' | 'children';
  data: RoomInputData;
  min: number;
  max: number;
  onCounterChange: (data: RoomInputCounterData) => void;
}

const RoomInputCounter = ({ id, category, data, min, max, onCounterChange, ...props }: IRoomInputCounterProps) => {
  const { t } = useTranslation();

  const value = useMemo(() => (category === 'adults' ? data.adults : data.children.length), [data, category]);

  const handleCounterAdd = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (category === 'children') {
      onCounterChange({
        category,
        data: [...data.children, { id: data.children.length, age: 0 }],
      });
    } else {
      onCounterChange({ category, data: value + 1 });
    }
  };

  const handleCounterRemove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (category === 'children') {
      onCounterChange({ category, data: [...data.children.slice(0, -1)] });
    } else {
      onCounterChange({ category, data: value - 1 });
    }
  };

  const handleSelect = (age: number, selectIndex: number) => {
    onCounterChange({
      category,
      data: [
        ...data.children.map((childrenData) =>
          childrenData.id === selectIndex ? { ...childrenData, age } : childrenData,
        ),
      ],
    });
  };

  return (
    <section className="room-input-counter-container" {...props}>
      <section className="room-input-counter-content">
        <article className="room-input-counter-label-wrapper">
          <span className="room-input-counter-label-title">{t(`counterInput.roomInput.counter.${category}`)}</span>
          {category === 'children' && (
            <span className="room-input-counter-label-description">
              {t('counterInput.roomInput.counter.childrenAgeAllowed')}
            </span>
          )}
        </article>

        <section className="room-input-counter-controller">
          <button
            className="room-input-counter-button"
            type="button"
            id="button-remove"
            disabled={value <= min}
            onClick={handleCounterRemove}
          >
            <Icon className="room-input-counter-button-icon" icon="remove" round />
          </button>

          <span className="room-input-counter-value">{value}</span>

          <button
            className="room-input-counter-button"
            type="button"
            id="button-add"
            disabled={value >= max}
            onClick={handleCounterAdd}
          >
            <Icon className="room-input-counter-button-icon" icon="add" round />
          </button>
        </section>
      </section>

      {category === 'children' && value > 0 && (
        <section className="room-input-counter-children-age-container">
          <span className="room-input-counter-children-age-title">
            {t('counterInput.roomInput.counter.childrenAgeTitle')}
          </span>

          <section className="room-input-counter-children-age-content-grid">
            {data.children.map((child) => (
              <ChildrenAgeSelect
                key={`room-${id}-children-age-select-${child.id + 1}`}
                id={`room-${id}-children-age-select-${child.id + 1}`}
                value={child.age}
                onSelect={handleSelect}
              />
            ))}
          </section>

          <div className="room-input-counter-children-age-alert">
            <Icon icon="alert" isCustom />
            <span>Informe a idade que a criança terá no check-out</span>
          </div>
        </section>
      )}
    </section>
  );
};

export default RoomInputCounter;
